import type { SourceNodeInfo, AISuggestion } from '../types/aiTypes'

type Listener = () => void

/**
 * Derived state for a specific node - this is what consumers actually need
 * By computing this derived state and comparing it, we can avoid re-renders
 * when the underlying data changes but the derived values stay the same.
 */
export interface AINodeDerivedState {
  isGenerating: boolean
  isReady: boolean // Has suggestions, not loading, not viewed
  hasSuggestions: boolean
  suggestionCount: number
}

/**
 * Pub/sub store for AI state that enables fine-grained subscriptions.
 *
 * The key optimization: Instead of passing sourceNodes through React context
 * (which causes all consumers to re-render on any change), we use a store
 * pattern where:
 *
 * 1. The store holds the source of truth (sourceNodes, suggestions)
 * 2. Components subscribe to changes via a listener
 * 3. Each component computes its own derived state and only re-renders
 *    if that derived state actually changed
 *
 * This means a change to node A's state won't cause node B to re-render,
 * even though they both subscribe to the same store.
 */
export class AIStateStore {
  private sourceNodes: Map<string, SourceNodeInfo> = new Map()
  private suggestions: AISuggestion[] = []
  private listeners = new Set<Listener>()

  /**
   * Update the source nodes map and notify all listeners
   */
  setSourceNodes(nodes: Map<string, SourceNodeInfo>): void {
    this.sourceNodes = nodes
    this.notify()
  }

  /**
   * Update suggestions and notify all listeners
   */
  setSuggestions(suggestions: AISuggestion[]): void {
    this.suggestions = suggestions
    this.notify()
  }

  /**
   * Batch update both sourceNodes and suggestions (single notification)
   */
  batchUpdate(nodes: Map<string, SourceNodeInfo>, suggestions: AISuggestion[]): void {
    this.sourceNodes = nodes
    this.suggestions = suggestions
    this.notify()
  }

  /**
   * Subscribe to state changes
   * @returns Unsubscribe function
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Get current source nodes (for snapshot in useSyncExternalStore pattern)
   */
  getSourceNodes(): Map<string, SourceNodeInfo> {
    return this.sourceNodes
  }

  /**
   * Get current suggestions
   */
  getSuggestions(): AISuggestion[] {
    return this.suggestions
  }

  /**
   * Compute derived state for a specific node
   * This is the key function that enables per-node optimization
   */
  getNodeDerivedState(nodeId: string): AINodeDerivedState {
    const info = this.sourceNodes.get(nodeId)

    if (!info) {
      return {
        isGenerating: false,
        isReady: false,
        hasSuggestions: false,
        suggestionCount: 0,
      }
    }

    return {
      isGenerating: info.isLoading,
      isReady: !info.isLoading && !info.viewed && info.suggestionCount > 0,
      hasSuggestions: info.suggestionCount > 0,
      suggestionCount: info.suggestionCount,
    }
  }

  /**
   * Get suggestions for a specific source node
   */
  getSuggestionsForNode(nodeId: string): AISuggestion[] {
    return this.suggestions.filter((s) => s.originalEdge?.source === nodeId)
  }

  /**
   * Notify all listeners of state change
   */
  private notify(): void {
    this.listeners.forEach((listener) => listener())
  }
}

/**
 * Compare two AINodeDerivedState objects for equality
 * Used to determine if a component should re-render
 */
export function areNodeStatesEqual(a: AINodeDerivedState | null, b: AINodeDerivedState): boolean {
  if (!a) return false
  return (
    a.isGenerating === b.isGenerating &&
    a.isReady === b.isReady &&
    a.hasSuggestions === b.hasSuggestions &&
    a.suggestionCount === b.suggestionCount
  )
}

/**
 * Create a new AIStateStore instance
 * Typically you'd create one per MindMapFlow and pass it down via context
 */
export function createAIStateStore(): AIStateStore {
  return new AIStateStore()
}
